// src/App.js
import React, { useState, useEffect } from "react";
import { ConfigProvider, Layout } from "antd";
import { Routes, Route, Link, Outlet } from "react-router-dom"; // <-- use Outlet for nested routes

// Import modules (not direct default imports) so we can adapt to default / named exports
import * as LandingPageModule from "./components/LandingPage";
import * as AdminAuthModule from "./components/admin/AdminAuth";
import * as AdminApprovalsModule from "./components/admin/AdminApprovals";
import * as AlumniRegistrationTabsModule from "./components/AlumniRegistrationTabs";
import * as ProfileSubmittedModule from "./components/ProfileSubmitted";
import * as DashboardModule from "./components/admin/Dashboard";
import * as SettingsModule from "./components/admin/Settings";
import * as AlumniListModule from "./components/admin/AlumniList";
import * as EventsModule from "./components/landing/Events";

import LandingPageHeader from "./components/landing/LandingPageHeader"; // persistent header
import Footer from "./components/landing/Footer"; // persistent footer

import { config } from "./config";

const { Content } = Layout;

/**
 * Helper: given an imported module object, resolve the component to use.
 * (kept your original helper)
 */
function resolveComponent(mod, fallbackName = "Component") {
  if (!mod) {
    console.warn(`[resolveComponent] module for ${fallbackName} is undefined or null`);
    return null;
  }

  let comp = mod.default ?? mod[fallbackName] ?? mod;

  if (typeof comp === "object" && comp !== null) {
    for (const key of Object.keys(comp)) {
      if (typeof comp[key] === "function") {
        comp = comp[key];
        break;
      }
    }
  }

  if (typeof comp !== "function" && typeof comp !== "object") {
    console.warn(
      `[resolveComponent] resolved ${fallbackName} to a non-component value:`,
      comp,
      "\nPlease check whether you exported the component as default or named export."
    );
  }

  return comp;
}

// Resolve components (safe)
const LandingPage = resolveComponent(LandingPageModule, "LandingPage");
const AdminAuth = resolveComponent(AdminAuthModule, "AdminAuth");
const AdminApprovals = resolveComponent(AdminApprovalsModule, "AdminApprovals");
const AlumniRegistrationTabs = resolveComponent(AlumniRegistrationTabsModule, "AlumniRegistrationTabs");
const ProfileSubmitted = resolveComponent(ProfileSubmittedModule, "ProfileSubmitted");
const Dashboard = resolveComponent(DashboardModule, "Dashboard");
const Settings = resolveComponent(SettingsModule, "Settings");
const AlumniList = resolveComponent(AlumniListModule, "AlumniList");

// Resolve Events properly (you previously imported EventsModule but didn't resolve it)
const Events = resolveComponent(EventsModule, "Events");

// 404 Fallback Page
function NotFound() {
  return (
    <div style={{ padding: 60, textAlign: "center" }}>
      <h1 style={{ fontSize: 48, marginBottom: 8 }}>404</h1>
      <p style={{ color: "#6b7280", fontSize: 18 }}>Page not found.</p>
      <p style={{ color: "#6b7280" }}>
        Try the <Link to="/">home page</Link> or check the URL.
      </p>
    </div>
  );
}

export default function App() {
  const [adminToken, setAdminToken] = useState(() => localStorage.getItem("admin_token") || null);

  useEffect(() => {
    if (adminToken) {
      console.debug("✅ Admin logged in, token:", adminToken);
    }
  }, [adminToken]);

  // Admin login handler
  const handleLogin = (token) => {
    if (token) {
      localStorage.setItem("admin_token", token);
      setAdminToken(token);
    }
  };

  // Admin logout handler
  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    setAdminToken(null);
  };

  // Helper to render a component safely (shows warning if missing)
  function SafeElement(Component, props = {}) {
    if (!Component) {
      console.warn("SafeElement: component is not available. Did you export it correctly?");
      return <div style={{ padding: 24 }}>Component not available — check console for details.</div>;
    }
    try {
      return <Component {...props} />;
    } catch (e) {
      console.error("SafeElement: failed to render component", e);
      return <div style={{ padding: 24 }}>Error rendering component — check console for details.</div>;
    }
  }

  /**
   * PersistentLayout
   * - Renders the landing header and footer once, and an <Outlet />
   * - All public routes (home, events, register, profile-submitted, etc.) are children of this route.
   */
  function PersistentLayout() {
    return (
      <>
        <LandingPageHeader />
        <main style={{ minHeight: "60vh" }}>
          <Outlet />
        </main>
        <Footer />
      </>
    );
  }

  return (
    <ConfigProvider theme={config.theme}>
      {/* Note: index.js already wraps with BrowserRouter (per your comment), so we don't add it here */}
      <Layout style={{ minHeight: "100vh" }}>
        <Content style={{ background: "#fff" }}>
          <Routes>
            {/* Public routes wrapped by PersistentLayout (header + footer persist) */}
            <Route path="/" element={<PersistentLayout />}>
              <Route index element={SafeElement(LandingPage)} />
              <Route path="register" element={SafeElement(AlumniRegistrationTabs)} />
              <Route path="profile-submitted" element={SafeElement(ProfileSubmitted)} />
              <Route path="events" element={SafeElement(Events)} />
              {/* add other public pages here as needed */}
            </Route>

            {/* Admin routes (keep separate; they won't render the public header/footer) */}
            <Route path="/admin/login" element={SafeElement(AdminAuth, { onLogin: handleLogin })} />
            <Route
              path="/admin/approvals"
              element={adminToken ? SafeElement(AdminApprovals) : <h2 style={{ padding: 24 }}>Please log in</h2>}
            />
            <Route
              path="/admin/dashboard"
              element={adminToken ? SafeElement(Dashboard) : <h2 style={{ padding: 24 }}>Please log in</h2>}
            />
            <Route
              path="/admin/settings"
              element={adminToken ? SafeElement(Settings, { onLogout: handleLogout }) : <h2 style={{ padding: 24 }}>Please log in</h2>}
            />
            <Route
              path="/admin/alumni-list"
              element={adminToken ? SafeElement(AlumniList) : <h2 style={{ padding: 24 }}>Please log in</h2>}
            />

            {/* Fallback route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Content>
      </Layout>
    </ConfigProvider>
  );
}
